import { Code } from '../common/code.model';
import { CodeList } from '../common/code-list.model';
import { CodeListElement, CodeListValueItem } from '../common/report-grid/model/code-list-element-model';

function deepClone<T>(x: T): T {
    return x ? JSON.parse(JSON.stringify(x)) : x;
}

export function normalizeLanguageIsoCode(languageIsoCode: string): string {
    if (!languageIsoCode) {
        return 'default';
    }

    return languageIsoCode.toLowerCase();
}

function getCodeLanguageIsoCode(code: Code): string {
    return normalizeLanguageIsoCode(
        (<any>code).languageIsoCode
        || (<any>code).languageISOCode
        || (<any>code).LanguageIsoCode
        || (<any>code).LanguageISOCode
        || 'default'
    );
}

function getLanguageCandidates(languageIsoCode: string, availableLanguages: string[] = []): string[] {
    const normalizedLanguage = normalizeLanguageIsoCode(languageIsoCode);
    const shortLanguage = normalizedLanguage.split('-')[0];
    const candidates: string[] = [];

    const addCandidate = (candidate: string) => {
        const normalizedCandidate = normalizeLanguageIsoCode(candidate);
        if (candidates.indexOf(normalizedCandidate) === -1) {
            candidates.push(normalizedCandidate);
        }
    };

    addCandidate(normalizedLanguage);
    if (shortLanguage && shortLanguage !== normalizedLanguage) {
        addCandidate(shortLanguage);
    }
    addCandidate('default');
    availableLanguages.forEach(addCandidate);

    return candidates;
}

function ensureLanguageContainers(codeListElement: CodeListElement) {
    if (!codeListElement.localizedValues) {
        codeListElement.localizedValues = {};
    }
}

export function ensureLocalizedCodeListElement(codeListElement: CodeListElement): CodeListElement {
    if (!codeListElement) {
        return codeListElement;
    }

    ensureLanguageContainers(codeListElement);

    if (!codeListElement.values) {
        codeListElement.values = {};
    }

    if (!codeListElement.valuesSorted) {
        codeListElement.valuesSorted = [];
    }

    const legacyValuesByLanguage = (<any>codeListElement).valuesByLanguage || {};
    const legacyValuesSortedByLanguage = (<any>codeListElement).valuesSortedByLanguage || {};
    const legacyLanguage = normalizeLanguageIsoCode((<any>codeListElement).languageIsoCode || 'default');

    if (legacyValuesByLanguage.default) {
        if (!Object.keys(codeListElement.values).length) {
            codeListElement.values = deepClone(legacyValuesByLanguage.default) || {};
        }
    }

    if (legacyValuesSortedByLanguage.default) {
        if (!codeListElement.valuesSorted.length) {
            codeListElement.valuesSorted = (deepClone(legacyValuesSortedByLanguage.default) || []).map(v => {
                return {
                    key: v.key,
                    value: v.value,
                    orderIndex: v.orderIndex,
                    languageIsoCode: 'default'
                };
            });
        }
    }

    if (!Object.keys(codeListElement.values).length && codeListElement.valuesSorted.length) {
        codeListElement.valuesSorted.forEach(v => {
            codeListElement.values[v.key] = v.value;
        });
    }

    Object.keys(legacyValuesByLanguage)
        .filter(language => normalizeLanguageIsoCode(language) !== 'default')
        .forEach(language => {
            codeListElement.localizedValues[normalizeLanguageIsoCode(language)] = deepClone(legacyValuesByLanguage[language]) || {};
        });

    Object.keys(legacyValuesSortedByLanguage)
        .filter(language => normalizeLanguageIsoCode(language) !== 'default')
        .forEach(language => {
            const normalizedLanguage = normalizeLanguageIsoCode(language);
            if (!codeListElement.localizedValues[normalizedLanguage]) {
                codeListElement.localizedValues[normalizedLanguage] = {};
            }

            (legacyValuesSortedByLanguage[language] || []).forEach(v => {
                codeListElement.localizedValues[normalizedLanguage][v.key] = v.value;
            });
        });

    if (!Object.keys(codeListElement.localizedValues).length && legacyLanguage !== 'default' && Object.keys(codeListElement.values).length) {
        codeListElement.localizedValues[legacyLanguage] = deepClone(codeListElement.values) || {};
    }

    codeListElement.valuesSorted = (codeListElement.valuesSorted || [])
        .map(v => {
            return {
                key: v.key,
                value: v.value,
                orderIndex: v.orderIndex,
                languageIsoCode: 'default'
            };
        })
        .sort((a, b) => a.orderIndex - b.orderIndex);

    return codeListElement;
}

export function ensureLocalizedCodeListMap(codeListMap: { [key: number]: CodeListElement })
    : { [key: number]: CodeListElement } {
    if (!codeListMap) {
        return {};
    }

    Object.keys(codeListMap).forEach(key => {
        ensureLocalizedCodeListElement(codeListMap[key]);
    });

    return codeListMap;
}

export function getLocalizedValues(codeListElement: CodeListElement, languageIsoCode: string): { [key: string]: string } {
    if (!codeListElement) {
        return {};
    }

    ensureLocalizedCodeListElement(codeListElement);

    const availableLanguages = Object.keys(codeListElement.localizedValues);
    const resolvedLanguage = getLanguageCandidates(languageIsoCode, availableLanguages)
        .find(candidate => !!codeListElement.localizedValues[candidate]);

    if (resolvedLanguage) {
        return deepClone(codeListElement.localizedValues[resolvedLanguage]) || {};
    }

    if (Object.keys(codeListElement.values).length) {
        return deepClone(codeListElement.values) || {};
    }

    const firstAvailableLanguage = availableLanguages[0];
    return firstAvailableLanguage ? deepClone(codeListElement.localizedValues[firstAvailableLanguage]) || {} : {};
}

export function getLocalizedValuesSorted(codeListElement: CodeListElement, languageIsoCode: string): CodeListValueItem[] {
    if (!codeListElement) {
        return [];
    }

    ensureLocalizedCodeListElement(codeListElement);

    const localizedValues = getLocalizedValues(codeListElement, languageIsoCode);

    if ((codeListElement.valuesSorted || []).length) {
        return (deepClone(codeListElement.valuesSorted) || []).map(v => {
            return {
                key: v.key,
                value: localizedValues[v.key] || v.value,
                orderIndex: v.orderIndex,
                languageIsoCode: localizedValues[v.key] ? normalizeLanguageIsoCode(languageIsoCode) : 'default'
            };
        });
    }

    return Object.keys(localizedValues).map((key, index) => {
        return {
            key,
            value: localizedValues[key],
            orderIndex: index + 1,
            languageIsoCode: normalizeLanguageIsoCode(languageIsoCode)
        };
    });
}

export function buildCodeListMap(codeLists: CodeList[]): { [key: number]: CodeListElement } {
    const codeListMap: { [key: number]: CodeListElement } = {};

    (codeLists || []).forEach((codeList, ix) => {
        const codeListElement = <CodeListElement>{
            codeListID: codeList.codeListId,
            name: codeList.codeListName,
            sortIndex: ix,
            values: {},
            valuesSorted: [],
            localizedValues: {}
        };

        (codeList.codes || []).forEach(code => {
            const itemLanguage = getCodeLanguageIsoCode(code);

            if (itemLanguage === 'default') {
                codeListElement.values[code.codeKey] = code.codeValue;
                codeListElement.valuesSorted.push(<CodeListValueItem>{
                    key: code.codeKey,
                    value: code.codeValue,
                    orderIndex: code.orderIndex,
                    languageIsoCode: 'default'
                });
                return;
            }

            if (!codeListElement.localizedValues[itemLanguage]) {
                codeListElement.localizedValues[itemLanguage] = {};
            }

            codeListElement.localizedValues[itemLanguage][code.codeKey] = code.codeValue;
        });

        codeListMap[codeList.codeListId] = ensureLocalizedCodeListElement(codeListElement);
    });

    return codeListMap;
}

export function getLocalizedCodes(codes: Code[], languageIsoCode: string): Code[] {
    if (!codes || !codes.length) {
        return [];
    }

    const groupedCodes: { [codeKey: string]: { [languageIsoCode: string]: Code } } = {};

    codes.forEach(code => {
        if (!groupedCodes[code.codeKey]) {
            groupedCodes[code.codeKey] = {};
        }

        groupedCodes[code.codeKey][getCodeLanguageIsoCode(code)] = code;
    });

    return Object.keys(groupedCodes)
        .map(codeKey => {
            const languageMap = groupedCodes[codeKey];
            const availableLanguages = Object.keys(languageMap);
            const resolvedLanguage = getLanguageCandidates(languageIsoCode, availableLanguages)
                .find(candidate => !!languageMap[candidate]);

            return deepClone(languageMap[resolvedLanguage || availableLanguages[0]]);
        })
        .filter(code => !!code)
        .sort((a, b) => a.orderIndex - b.orderIndex);
}